/**
 * MyBusinessSummary — the 💼 My Business zone on the home page.
 *
 * Ship 3 of the My Business plan built this as the sticky third-column band;
 * Ship 1 of the widget board (Aug 25 2026) re-homed it as the full-width
 * REVIEW LAYER below the widget board — Greg: "set up to get to activity, to
 * work, and then they can review stuff if they want." Tiles read Past ·
 * Present · Future (his original framing for the dashboard).
 *
 * Money is self-only and reuses the finance board's groupByPerson engine via
 * the business snapshot — the numbers here reconcile to the penny with
 * My Pipeline → Commissions and Chad's board. Field roles never see
 * supplement dollars (sanitized in the data layer). The snapshot is fetched
 * ONCE in HomeRedesign and passed down so the strip, board, and this zone
 * share a single load.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import type { TeamMember } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import { logCardClick } from "../../lib/uiEvents";
import { FIELD_ROLES, todayET } from "../../lib/businessSnapshot";
import { readCachedBand, type CoreSnapshot } from "../home/useBusinessSnapshot";
import type { aggregateMatch } from "../../lib/commitmentMath";
import PlanChangedSheet from "../home/PlanChangedSheet";

// Counts read "3" or "2.5" — split credit is 0.5, never round it away.
const fmtHalf = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

function Sparkline({ series }: { series: number[] }) {
  if (series.length < 2 || series.every((v) => v === 0)) return null;
  const max = Math.max(...series, 1);
  const w = 44, h = 14;
  const pts = series
    .map((v, i) => `${(i / (series.length - 1)) * w},${h - (v / max) * (h - 2) - 1}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="opacity-70" aria-hidden="true">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function MyBusinessSummary({
  member, role, core, snapshotError,
  periodLabel, periodMeet, periodBuild,
  commitStats, commitLabel, streak, showCommit, weekFinal,
}: {
  member: TeamMember;
  role: string | null | undefined;
  /** Business snapshot core, fetched once in HomeRedesign and shared. */
  core: CoreSnapshot | null;
  snapshotError: string | null;
  periodLabel: string;
  periodMeet: number;
  periodBuild: number;
  commitStats: ReturnType<typeof aggregateMatch> | null;
  commitLabel: string; // "Your 1-on-1" or "<Rep>'s 1-on-1"
  streak: number;
  showCommit: boolean;
  weekFinal: boolean; // viewing a past week — commitment % is final
}) {
  const isFieldMoney = !!role && FIELD_ROLES.has(role);
  const cached = core ? null : readCachedBand(member.id);
  const [planOpen, setPlanOpen] = useState(false);

  const nowMonth = todayET().slice(0, 7);
  const thisMonthPaid = core
    ? core.earnings.rows.find((r) => r.month === nowMonth)?.paid ?? 0
    : cached?.earnedThisMonth ?? 0;
  const series = core ? core.earnings.rows.map((r) => r.paid) : cached?.monthSeries ?? [];
  const coming = core
    ? core.outlook.pendingQueue + core.outlook.approvedAwaitingPay + core.outlook.projectedSignedUnbuilt
    : cached?.coming ?? 0;
  const bookValue = core ? core.pipeline.bookValue : cached?.bookValue ?? 0;
  const liveCount = core ? core.pipeline.liveCount : cached?.liveCount ?? 0;
  const loading = !core && !cached;

  const goalOf = (k: "apps" | "claims" | "builds_scheduled" | "builds") => core?.goals?.[k];
  const funnelCell = (label: string, val: number, goalKey: "apps" | "claims" | "builds_scheduled" | "builds") => {
    const g = goalOf(goalKey);
    const hit = g != null && g > 0 && val >= g;
    return (
      <div key={goalKey} className="text-center">
        <div className={"text-base font-extrabold leading-none " + (hit ? "text-emerald-600" : "text-bardin-ink")}>
          {fmtHalf(val)}
          {g != null && g > 0 && <span className="text-[10px] font-bold text-slate-400">/{fmtHalf(g)}</span>}
        </div>
        <div className="text-[9px] text-slate-500 font-semibold mt-0.5 uppercase tracking-wide">{label}</div>
      </div>
    );
  };

  const eyebrow = (t: string) => (
    <div className="text-[8px] font-extrabold uppercase tracking-[0.14em] text-bardin-orange">{t}</div>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-6">
      {/* ── Zone header — the review layer's identity ── */}
      <div className="flex items-baseline gap-2.5 flex-wrap mb-3">
        <span className="font-extrabold text-[15px] text-bardin-ink">💼 My Business</span>
        <span className="text-[11px] text-slate-500 font-medium">
          your money · your results · private to you
        </span>
        {isFieldMoney && (
          <span className="ml-auto flex gap-3 text-[11px] font-bold">
            <Link to="/my-business" onClick={() => logCardClick("mybiz_link_now")} className="text-bardin-orange">Now</Link>
            <Link to="/my-business?tab=past" onClick={() => logCardClick("mybiz_link_past")} className="text-bardin-orange">Past</Link>
            <Link to="/my-business?tab=lenses" onClick={() => logCardClick("mybiz_link_lenses")} className="text-bardin-orange">Lenses</Link>
          </span>
        )}
      </div>

      {snapshotError && <div className="text-[10px] text-rose-500 mb-2">Snapshot didn't load — {snapshotError}</div>}

      <div className={`lg:grid lg:gap-6 ${isFieldMoney ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
        {/* ── Column A: money tiles (Past · Present · Future) + chips —
            field-money roles only; the column is omitted entirely otherwise
            so non-field viewers don't get a blank first third. ── */}
        {isFieldMoney && (
        <div>
          {isFieldMoney && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Link to="/my-business?tab=past" onClick={() => logCardClick("mybiz_tile_earned")}
                className={"rounded-xl border border-slate-200 p-2.5 hover:border-emerald-300 transition " + (loading ? "animate-pulse" : "")}>
                {eyebrow("Past")}
                <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Earned · {periodLabel === "This month" ? "mo" : "this mo"}</div>
                <div className="text-lg font-extrabold text-emerald-600 leading-tight">{loading ? "—" : fmtUSD(thisMonthPaid)}</div>
                <div className="text-emerald-600"><Sparkline series={series} /></div>
              </Link>
              <Link to="/my-pipeline" onClick={() => logCardClick("mybiz_tile_pipeline")}
                className={"rounded-xl border border-orange-200 bg-orange-50/40 p-2.5 hover:border-bardin-orange transition " + (loading ? "animate-pulse" : "")}>
                {eyebrow("Present")}
                <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Pipeline</div>
                <div className="text-lg font-extrabold text-bardin-orange leading-tight">{loading ? "—" : liveCount}</div>
                <div className="text-[9px] text-slate-400 leading-tight mt-0.5">{loading ? "live deals" : `${fmtUSD(bookValue)} est. value`}</div>
              </Link>
              <Link to="/my-business" onClick={() => logCardClick("mybiz_tile_coming")}
                className={"rounded-xl border border-slate-200 p-2.5 hover:border-bardin-teal transition " + (loading ? "animate-pulse" : "")}>
                {eyebrow("Future")}
                <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Coming</div>
                <div className="text-lg font-extrabold text-bardin-teal leading-tight">{loading ? "—" : fmtUSD(coming)}</div>
                <div className="text-[9px] text-slate-400 leading-tight mt-0.5">
                  {core ? `incl. ${fmtUSD(core.outlook.projectedSignedUnbuilt)} projected` : "queue + projected"}
                </div>
              </Link>
            </div>
          )}
          {isFieldMoney && core && (core.heldCount > 0 || core.coldCount > 0 || core.drawBalance > 0) && (
            <div className="flex flex-wrap gap-1.5 mb-3 text-[10px] font-bold">
              {core.heldCount > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">🔒 {core.heldCount} held</span>
              )}
              {core.coldCount > 0 && (
                <Link to="/my-pipeline" className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">🥶 {core.coldCount} going cold</Link>
              )}
              {core.drawBalance > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">💵 {fmtUSD(core.drawBalance)} draw out</span>
              )}
            </div>
          )}
        </div>
        )}

        {/* ── Column B: week pulse + 1-on-1 commitment ── */}
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-2xl font-extrabold text-bardin-teal leading-none">{periodMeet}</div>
              <div className="text-[11px] text-slate-500 font-semibold mt-1">Adjuster meetings</div>
            </div>
            <div>
              <div className="text-2xl font-extrabold text-bardin-orange leading-none">{periodBuild}</div>
              <div className="text-[11px] text-slate-500 font-semibold mt-1">Builds</div>
            </div>
          </div>
          {showCommit && commitStats && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">{commitLabel}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold">
                  {commitStats.showed}/{commitStats.committed} slots
                  {commitStats.onPacePct != null && ` · ${commitStats.onPacePct}% ${weekFinal ? "final" : "on pace"}`}
                </span>
                {streak > 0 && (
                  <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-lg px-2 py-1 text-white" style={{ background: "linear-gradient(90deg,#f46530,#ff9e7a)" }}>
                    🔥 {streak}-wk
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Link to="/canvas" className="text-[11px] font-bold text-bardin-teal border border-slate-200 rounded-lg px-2.5 py-1.5">Open Canvas</Link>
                {core?.commitmentRow?.locked_at && role && FIELD_ROLES.has(role) && (
                  <button onClick={() => { logCardClick("mybiz_plan_changed"); setPlanOpen(true); }}
                    className="text-[11px] font-bold text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                    📅 Plan changed?
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Column C: funnel vs goals + crew row + full snapshot ── */}
        <div>
          {/* Mobile-only: on desktop the Spiff strip's Goals card (Mine scope)
              shows the identical four numbers vs the identical targets —
              rendering both would duplicate. Phones keep this because the
              strip collapses there. */}
          {isFieldMoney && core && (
            <div className="mt-3 pt-3 border-t border-slate-100 lg:hidden">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">This week's funnel</div>
              <div className="grid grid-cols-4 gap-1">
                {funnelCell("Apps", core.funnel.apps, "apps")}
                {funnelCell("Claims", core.funnel.claims, "claims")}
                {funnelCell("Signed", core.funnel.signedIA, "builds_scheduled")}
                {funnelCell("Builds", core.funnel.builds, "builds")}
              </div>
            </div>
          )}

          {core && core.crew.of > 0 && (
            <Link to="/crew" onClick={() => logCardClick("mybiz_crew_row")}
              className="mt-3 pt-3 border-t border-slate-100 lg:first:mt-0 lg:first:pt-0 lg:first:border-t-0 flex items-center justify-between gap-2 group">
              <div className="flex items-center gap-2 flex-wrap text-[11px] font-bold text-slate-600">
                <span className="text-sm">🏆</span>
                {core.crew.rank != null && <span>#{core.crew.rank} of {core.crew.of}</span>}
                <span className="text-slate-400">·</span>
                <span>{fmtHalf(core.crew.pointsWeek)} pts</span>
                {isFieldMoney && (
                  <>
                    <span className="text-slate-400">·</span>
                    <span title="Perfect Week — claims + signed IAs, target 4">PW {fmtHalf(core.crew.perfectTotal)}/4</span>
                  </>
                )}
                {core.crew.spiffWonWeek > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{fmtUSD(core.crew.spiffWonWeek)} SPIFF</span>
                )}
              </div>
              <span className="text-slate-300 text-lg group-hover:text-slate-400">›</span>
            </Link>
          )}

          {isFieldMoney && (
            <Link to="/my-business" onClick={() => logCardClick("mybiz_full_snapshot")}
              className="mt-3 block text-center text-[11px] font-bold text-bardin-teal border-t border-slate-100 pt-2.5 lg:first:mt-0 lg:first:pt-0 lg:first:border-t-0">
              Full snapshot ›
            </Link>
          )}
        </div>
      </div>

      {core?.commitmentRow && (
        <PlanChangedSheet
          open={planOpen}
          onClose={() => setPlanOpen(false)}
          commitmentId={core.commitmentRow.id}
          currentBlocks={core.commitmentRow.blocks}
          weekStart={core.commitmentRow.week_start}
          repId={member.id}
          repName={member.full_name ?? "Rep"}
          onSubmitted={() => setPlanOpen(false)}
        />
      )}
    </div>
  );
}
